import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import {
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { ConfirmDialogComponent } from '@app/shared/confirm-dialog/confirm-dialog.component';
import { ContentBoxComponent } from '@app/shared/content-box/content-box.component';
import { CustomPaginatorIntl } from '@app/shared/custom-paginator/custom-paginator.component';
import { Labels } from '@core/constants/labels';
import { FilterDataMonitoredCases } from '@core/models/filter-data-monitored-cases.models';
import { MonitoredCase } from '@core/models/monitored-case.model';
import { ExamService } from '@core/services/exam-service/exam-service.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription, fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-cases-monitored',
  standalone: true,
  imports: [
    MatButtonModule,
    MatInputModule,
    MatTableModule,
    MatPaginatorModule,
    ContentBoxComponent,
    MatProgressSpinnerModule,
    CommonModule,
    FormsModule,
    ConfirmDialogComponent,
  ],
  providers: [{ provide: MatPaginatorIntl, useClass: CustomPaginatorIntl }],
  templateUrl: './cases-monitored.component.html',
  styleUrl: './cases-monitored.component.scss',
})
export class CasesMonitoredComponent implements OnDestroy {
  subscriptions = new Subscription();
  caseList: MonitoredCase[] = [];
  dataSource: MatTableDataSource<MonitoredCase>;

  hidePageSize = true;
  showFirstLastButtons = true;

  count = 0;
  pageSize = 10;
  pageIndex = 0;

  currentNewCode: string = '';

  loading = true;
  filterText = '';

  labels = Labels.MONITORED_CASES;

  showingExamAdder = false;

  displayedColumns: string[] = ['code', 'open', 'delete'];

  constructor(
    private examService: ExamService,
    private router: Router,
    private toastrService: ToastrService,
    private dialog: MatDialog,
  ) {
    this.dataSource = new MatTableDataSource<MonitoredCase>([]);
  }

  ngOnInit() {
    this.updateList();
    const resizeObservable = fromEvent(window, 'resize');
    const resizeSubscription = resizeObservable
      .pipe(
        debounceTime(300), // Debounce to avoid excessive calls
      )
      .subscribe(() => {
        this.updateList();
      });
    this.subscriptions.add(resizeSubscription);
  }

  updateList() {
    this.loading = true;
    const currentFilterData = new FilterDataMonitoredCases();
    const displayHeight = window.innerHeight;
    this.pageSize = Math.floor((16 * (displayHeight - 350)) / 1174);
    this.pageSize = Math.max(1, this.pageSize);
    currentFilterData.page_size = this.pageSize;
    currentFilterData.code = this.filterText;
    // Ensure the pageSize is at least 1
    currentFilterData.page = this.pageIndex;

    const sub = this.examService
      .getMonitoredExams(currentFilterData)
      .subscribe((out: any) => {
        this.count = out.count;
        this.caseList = out.results as MonitoredCase[];
        this.dataSource.data = this.caseList;
        this.loading = false;
      });
    this.subscriptions.add(sub);
  }

  handlePageEvent(e: PageEvent) {
    this.pageSize = e.pageSize;
    this.pageIndex = e.pageIndex;

    this.updateList();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  saveNewExam() {
    if (this.currentNewCode === '') return;
    const newCase = this.examService
      .saveMonitoredExam(this.currentNewCode)
      .subscribe((data: any) => {
        this.toastrService.success(this.labels.SUCCESS_ADD);
        this.currentNewCode = '';
        this.updateList();
      });
    this.subscriptions.add(newCase);
  }

  filterList() {
    this.updateList();
  }

  showAdder() {
    this.showingExamAdder = true;
  }

  hideAdder() {
    this.showingExamAdder = false;
  }

  openExam(code: string) {
    this.router.navigate([`exam_view/${code}`]);
  }

  actuallyDeleteExam(id: number) {
    this.examService.deleteMonitoredExam(id).subscribe(
      (x) => {
        this.toastrService.success(this.labels.SUCCESS_DELETE);
        this.updateList();
      },
      (err) => {
        this.toastrService.error(`${this.labels.FAILED_DELETE}`);
      },
    );
  }

  deleteExam(id: number) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.labels.DELETE_EXAM_CONFIRMATION,
        message: this.labels.DELETE_EXAM_CONFIRMATION_TEXT,
        confirmText: this.labels.DELETE_EXAM_CONFIRM,
        cancelText: this.labels.DELETE_EXAM_CANCEL,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.actuallyDeleteExam(id);
      }
    });
  }
}
